import type { Graphics } from 'miglib';
import { globals } from './types';

export class RenderUtil {

    static formatDuration(start: number, end: number): string {
        const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
        parts.push(`${seconds}s`);

        return parts.join(' ');
    }

    static lerp(start: number, end: number, t: number): number {
        return start + (end - start) * t;
    }

    private static tileSpan(a: number, b: number, tile: number): { pos: number; len: number; frac: number }[] {
        const out: { pos: number; len: number; frac: number }[] = [];
        if (b - a <= 0 || tile <= 0) return out;
        let prev = a;
        let k = 1;
        while (prev < b) {
            const next = Math.min(b, a + Math.round(k * tile));
            const len = next - prev;
            if (len > 0) out.push({ pos: prev, len, frac: len / tile });
            prev = next;
            k++;
        }
        return out;
    }

    static render9Slice(
        g: Graphics,
        x: number,
        y: number,
        w: number,
        h: number,
        scale: number = 1
    ): { width: number; height: number } {

        const image = "panel_" + globals.panelColor;

        const cW = 6;
        const cH = 6;
        const midW = 12;
        const midH = 12;

        const sRightX = cW + midW;
        const sBotY = cH + midH;

        const cDestW = cW * scale;
        const cDestH = cH * scale;
        const midDestW = midW * scale;
        const midDestH = midH * scale;

        const X0 = Math.round(x);
        const Y0 = Math.round(y);
        const X3 = Math.round(x + w);
        const Y3 = Math.round(y + h);
        const boxW = X3 - X0;
        const boxH = Y3 - Y0;

        const leftW = Math.min(Math.round(cDestW), Math.floor(boxW / 2));
        const rightW = Math.min(Math.round(cDestW), boxW - leftW);
        const topH = Math.min(Math.round(cDestH), Math.floor(boxH / 2));
        const botH = Math.min(Math.round(cDestH), boxH - topH);

        const X1 = X0 + leftW;
        const X2 = X3 - rightW;
        const Y1 = Y0 + topH;
        const Y2 = Y3 - botH;

        const fL = cDestW ? leftW / cDestW : 1;
        const fR = cDestW ? rightW / cDestW : 1;
        const fT = cDestH ? topH / cDestH : 1;
        const fB = cDestH ? botH / cDestH : 1;

        const rightSrcX = sRightX + cW * (1 - fR);
        const botSrcY = sBotY + cH * (1 - fB);

        g.renderImage(image, X0, Y0, { sx: 0, sy: 0, sw: cW * fL, sh: cH * fT, w: leftW, h: topH });
        g.renderImage(image, X2, Y0, { sx: rightSrcX, sy: 0, sw: cW * fR, sh: cH * fT, w: rightW, h: topH });
        g.renderImage(image, X0, Y2, { sx: 0, sy: botSrcY, sw: cW * fL, sh: cH * fB, w: leftW, h: botH });
        g.renderImage(image, X2, Y2, { sx: rightSrcX, sy: botSrcY, sw: cW * fR, sh: cH * fB, w: rightW, h: botH });

        const hs = RenderUtil.tileSpan(X1, X2, midDestW);
        const vs = RenderUtil.tileSpan(Y1, Y2, midDestH);

        for (const t of hs) {
            g.renderImage(image, t.pos, Y0, { sx: cW, sy: 0, sw: midW * t.frac, sh: cH * fT, w: t.len, h: topH });
            g.renderImage(image, t.pos, Y2, { sx: cW, sy: botSrcY, sw: midW * t.frac, sh: cH * fB, w: t.len, h: botH });
        }

        for (const v of vs) {
            g.renderImage(image, X0, v.pos, { sx: 0, sy: cH, sw: cW * fL, sh: midH * v.frac, w: leftW, h: v.len });
            g.renderImage(image, X2, v.pos, { sx: rightSrcX, sy: cH, sw: cW * fR, sh: midH * v.frac, w: rightW, h: v.len });
        }

        for (const t of hs) {
            for (const v of vs) {
                g.renderImage(image, t.pos, v.pos, { sx: cW, sy: cH, sw: midW * t.frac, sh: midH * v.frac, w: t.len, h: v.len });
            }
        }

        return { width: boxW, height: boxH };
    }
}